// The phone's back gesture closes the topmost overlay (row #35, 2026-10-19).
//
// In the installed PWA there's no browser back button, and Android's back gesture leaves the page (or the app)
// when an overlay never added a history entry. Each overlay that should close on back calls closeOnBack(): it
// adds one history entry (same URL), and a back (popstate) that drops below it closes the overlay. Overlays nest
// (the scan viewer, then its full screen): each closes only when the history drops below its own depth, so one
// back closes one overlay. Closing an overlay from its own button calls the returned release(), which removes
// its history entry again (history.back) so back doesn't then land on a stale entry.

type Entry = { depth: number; close: () => void };

const KEY = "wrapOverlayDepth";
const open: Entry[] = [];
let listening = false;
/** history.back() calls of our own still to arrive (their popstate closes nothing) */
let ownBacks = 0;

function depthNow(): number {
  const s = history.state as Record<string, unknown> | null;
  return typeof s?.[KEY] === "number" ? (s[KEY] as number) : 0;
}

function onPop(): void {
  if (ownBacks > 0) {
    ownBacks--;
    return;
  }
  const d = depthNow();
  // close every overlay above where history now is, topmost first
  for (let i = open.length - 1; i >= 0; i--) {
    if (open[i].depth > d) {
      const [e] = open.splice(i, 1);
      e.close();
    }
  }
}

/** Make the back gesture call `close`. Returns release(): call it when the overlay is closed some other way. */
export function closeOnBack(close: () => void): () => void {
  if (!listening) {
    window.addEventListener("popstate", onPop);
    listening = true;
  }
  const depth = Math.max(depthNow(), open.length ? open[open.length - 1].depth : 0) + 1;
  const entry: Entry = { depth, close };
  try {
    history.pushState({ ...(history.state as object | null), [KEY]: depth }, "");
  } catch {
    return () => undefined;
  }
  open.push(entry);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const k = open.indexOf(entry);
    if (k < 0) return;              // already closed by a back
    open.splice(k, 1);
    if (depthNow() - ownBacks === depth) {   // (a release just before ours may still be going back)
      ownBacks++;
      history.back();
    }
  };
}
